import React, { useState, useEffect } from 'react';
import { getUserRings, getUserActiveRing, setUserActiveRing, getRingsByCategory, RING_CATEGORIES, ALL_RINGS } from '../services/profileRingService';
import ProfileRing from './ProfileRing';

function RingSelectionModal({ isOpen, onClose, userId }) {
  const [ownedRings, setOwnedRings] = useState([]);
  const [activeRingId, setActiveRingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [ringsByCategory, setRingsByCategory] = useState({});

  useEffect(() => {
    if (!isOpen || !userId) return;

    const loadRingData = async () => {
      setLoading(true);
      try {
        const [rings, activeRing] = await Promise.all([
          getUserRings(userId),
          getUserActiveRing(userId)
        ]);

        setOwnedRings(rings);
        setActiveRingId(activeRing);
        setRingsByCategory(getRingsByCategory(rings));
      } catch (error) {
        console.error('Error loading ring data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadRingData();
  }, [isOpen, userId]);

  const handleRingSelect = async (ringId) => {
    try {
      const success = await setUserActiveRing(userId, ringId);
      if (success) {
        setActiveRingId(ringId);
      }
    } catch (error) {
      console.error('Error setting active ring:', error);
    }
  };

  const handleRemoveRing = async () => {
    try {
      const success = await setUserActiveRing(userId, null);
      if (success) {
        setActiveRingId(null);
      }
    } catch (error) {
      console.error('Error removing ring:', error);
    }
  };

  const getCategoryName = (category) => {
    switch (category) {
      case RING_CATEGORIES.MILESTONE:
        return '🏆 Milestone Rings';
      case RING_CATEGORIES.PURCHASEABLE:
        return '💎 Store Rings';
      case RING_CATEGORIES.ADMIN_SPECIAL:
        return '⭐ Special Rings';
      default:
        return 'All Rings';
    }
  };

  const getFilteredRings = () => {
    if (selectedCategory === 'all') {
      return ownedRings;
    }
    return ringsByCategory[selectedCategory] || [];
  };

  if (!isOpen) return null;

  return (
    <div className="ring-modal-overlay" onClick={onClose}>
      <div className="ring-modal-container" onClick={(e) => e.stopPropagation()}>
        <div className="ring-modal-header">
          <h2>Profile Rings</h2>
          <button className="ring-modal-close" onClick={onClose}>×</button>
        </div>

        {loading ? (
          <div className="ring-modal-loading">
            <div className="emoji-loader">
              <span className="emoji-item">💍</span>
              <span className="emoji-item">✨</span>
              <span className="emoji-item">🎨</span>
            </div>
            <p>Loading rings...</p>
          </div>
        ) : (
          <>
            {/* Current Active Ring */}
            <div className="current-ring-section">
              <h3>Current Ring</h3>
              <div className="current-ring-display">
                <ProfileRing ringId={activeRingId} size="large">
                  <div className="demo-avatar">
                    <span>👤</span>
                  </div>
                </ProfileRing>
                <div className="current-ring-info">
                  {activeRingId ? (
                    <>
                      <div className="ring-name">{ALL_RINGS[activeRingId]?.name}</div>
                      <button 
                        className="remove-ring-button"
                        onClick={handleRemoveRing}
                      >
                        Remove
                      </button>
                    </>
                  ) : (
                    <div className="no-ring-text">No ring selected</div>
                  )}
                </div>
              </div>
            </div>

            {/* Category Filters */}
            <div className="ring-category-filters">
              <button 
                className={`category-filter ${selectedCategory === 'all' ? 'active' : ''}`}
                onClick={() => setSelectedCategory('all')}
              >
                All ({ownedRings.length})
              </button>
              {Object.values(RING_CATEGORIES).map(category => {
                const categoryRings = ringsByCategory[category] || [];
                return (
                  <button 
                    key={category}
                    className={`category-filter ${selectedCategory === category ? 'active' : ''}`}
                    onClick={() => setSelectedCategory(category)}
                  >
                    {getCategoryName(category)} ({categoryRings.length})
                  </button>
                );
              })}
            </div>

            {/* Ring Grid */}
            <div className="ring-grid-section">
              {getFilteredRings().length > 0 ? (
                <div className="ring-grid">
                  {getFilteredRings().map(ring => {
                    const ringData = ALL_RINGS[ring.id];
                    const isActive = activeRingId === ring.id;
                    
                    return (
                      <div 
                        key={ring.id} 
                        className={`ring-option ${isActive ? 'active' : ''}`}
                        onClick={() => handleRingSelect(ring.id)}
                      >
                        <ProfileRing ringId={ring.id} size="medium">
                          <div className="demo-avatar-small">
                            <span>👤</span>
                          </div>
                        </ProfileRing>
                        
                        <div className="ring-option-info">
                          <div className="ring-option-name">{ringData?.name}</div>
                          <div className="ring-option-description">{ringData?.description}</div>
                          <div className={`ring-option-rarity rarity-${ringData?.rarity}`}>
                            {ringData?.rarity?.charAt(0).toUpperCase() + ringData?.rarity?.slice(1)}
                          </div>
                          
                          {ring.unlockedAt && (
                            <div className="ring-unlocked-date">
                              Unlocked {new Date(ring.unlockedAt).toLocaleDateString()}
                            </div>
                          )}
                        </div>
                        
                        {isActive && (
                          <div className="active-ring-indicator">
                            <span>✓</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="no-rings-message">
                  <div className="no-rings-icon">💍</div>
                  <div className="no-rings-text">
                    {selectedCategory === 'all' 
                      ? 'No rings unlocked yet. Start engaging to earn milestone rings or visit the store!'
                      : `No ${getCategoryName(selectedCategory).toLowerCase()} unlocked yet.`
                    }
                  </div>
                </div>
              )}
            </div>

            {/* Modal Actions */}
            <div className="ring-modal-actions">
              <button className="ring-modal-done-button" onClick={onClose}>
                Done
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default RingSelectionModal;